import React, { useState, useEffect } from 'react';
import { 
  Stack, 
  Title, 
  Paper, 
  Group, 
  Text, 
  Badge, 
  Button, 
  Image, 
  Loader,
  Center 
} from '@mantine/core';
import { IconPlus } from '@tabler/icons-react';
import { useSalon } from '../../context/SalonContext';
import { useApp } from '../../context/AppContext';
import DeviceRegistrationModal from './DeviceRegistrationModal';
import terminalImage from '../../images/stripe-wisepos-e-terminal.jpg';

function Devices() {
  const [error, setError] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const { businessInfo, setBusinessInfo, currentUser } = useSalon();
  const { registerStripeConnectTerminalDevice, updateSalon, getStripeConnectTerminalDevice } = useApp();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [newDeviceData, setNewDeviceData] = useState({
    label: '',
    registrationCode: ''
  });
  const [deviceData, setDeviceData] = useState(null);
  
  const isPrimaryOwner = currentUser?.id === businessInfo?.primary_owner_id;

  useEffect(() => {
    if (businessInfo?.stripe_terminal_device_id && !deviceData) {
      getStripeConnectTerminalDevice(businessInfo.stripe_terminal_device_id, businessInfo.stripe_connect_account_id)
        .then((deviceData) => {
          setDeviceData(deviceData);
          setIsLoading(false);
        });
    } else if (businessInfo) {
      setIsLoading(false);
    }
  }, [businessInfo]);

  const deviceTypeToLabel = {
    "simulated_wisepos_e": "WisePOS E (Simulated)",
    "wisepos_e": "WisePOS E",
  };

  const handleSubmit = async () => {
    setIsLoading(true);
    try {
      const response = await registerStripeConnectTerminalDevice(newDeviceData, businessInfo);
      
      if (response?.id) {
        const businessInfoWithStripeTerminalDeviceId = {
          ...businessInfo,
          stripe_terminal_device_id: response.id
        };
        await updateSalon(businessInfoWithStripeTerminalDeviceId);
        setBusinessInfo(businessInfoWithStripeTerminalDeviceId);
        setIsModalOpen(false);
        setIsLoading(false);

        setDeviceData(response);
      }
    } catch (error) {
      console.error('Error registering device:', error);
      setIsLoading(false);
      setError("Unexpected error occurred while registering device.  Please try again.  If the problem persists, contact support at contact@getpagoda.com");
    }
  };

  const getStatusColor = (status) => status === 'online' ? 'green' : 'red';

  if (isLoading) {
    return (
      <Center mt="lg">
        <Loader color="violet" size="lg" />
      </Center>
    );
  }

  return (
    <Stack spacing="md" p="md">
      {error && <Text size="md" ta="center" mb="xl" c="red">{error}</Text>}

      <Group position="apart">
        <Title order={3}>Card Reader Devices</Title>
        {isPrimaryOwner && !deviceData && (
          <Button 
            leftIcon={<IconPlus size="1rem" />}
            onClick={() => setIsModalOpen(true)}
            color="violet"
          >
            Add New Device
          </Button>
        )}
      </Group>

      {deviceData ? (
        <Paper p="md" withBorder>
          <Group position="apart" align="flex-start">
            <Group>
              <Image 
                src={terminalImage} 
                w={150} 
                alt="Stripe Terminal"
              />
              <Stack gap="xs">
                <Group>
                  <Text size="lg" fw={500}>{deviceData?.label}</Text>
                  <Badge color={getStatusColor(deviceData?.status)} variant="light">
                    {deviceData?.status.toUpperCase()}
                  </Badge>
                </Group>
                <Text size="sm" c="dimmed">Device Type: {deviceTypeToLabel[deviceData?.device_type]}</Text>
                <Text size="sm" c="dimmed">Serial Number: {deviceData?.serial_number}</Text>
              </Stack>
            </Group>
          </Group>
        </Paper>
      ) : (
        <Stack gap="md">
          <Text c="dimmed">
            We noticed you do not have a POS device registered to your account. If you already 
            have an existing Stripe WisePOS™ E device, please register it to begin collecting payments.
          </Text>
          <Text c="dimmed">
            Otherwise, please call us during business hours (Monday-Friday, 9am-5pm) at 
            (617) 237-6592 or email us at{' '}
            <Text component="a" href={`mailto:sales@getpagoda.com?subject=POS Order - ${businessInfo?.salon_name}`} c="violet">
              sales@getpagoda.com
            </Text>
            {' '}with subject "POS Order - {businessInfo?.salon_name}" and we'll help you get set up.
          </Text>
        </Stack>
      )}

      <DeviceRegistrationModal
        opened={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        onSubmit={handleSubmit}
        deviceData={newDeviceData}
        setDeviceData={setNewDeviceData}
        isLoading={isLoading}
      />
    </Stack>
  );
}

export default Devices; 